// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { H4 } from '@components/shared'
import { Col, Row, Container, config, Hidden } from 'react-awesome-styled-grid'
import LinkifyText from '@components/LinkifyText'
import BackgroundGradient from '@components/BackgroundGradient'

type QuestionType = {
  title: string,
  answer: string
}

const questions: QuestionType[] = [
  {
    title: 'Why should I give someone the gift of sexy audio stories?',
    answer: 'We get it: a year of Dipsea isn’t exactly the average gift. It’s a thoughtful and generous invitation to go explore. If you’re thinking, “They’ll love this,” we think you’re right. Keep reading.'
  },
  {
    title: 'How does the recipient receive the gift?',
    answer: 'Your recipient will receive a branded email with a unique subscription code. You can also opt to send the gift email to yourself so you can share the gift with them however you’d like!'
  },
  {
    title: 'What if I want to give Dipsea anonymously, is that a thing?',
    answer: 'Cheeky! That’s totally a thing. You’ll have the option to enter whatever you’d like in the “from” field. ”Anonymous”, “Your secret admirer,” or even just “You’re welcome!”.'
  },
  {
    title: 'What do they get with their subscription?',
    answer: 'Unlimited access to hundreds of sexy audio stories, written stories, sleep scenes and wellness sessions, with new content added each week. Whether your recipient is straight, bi, queer, or still exploring, there’s something for everyone on Dipsea.'
  },
  {
    title: 'Who would benefit from getting Dipsea?',
    answer: 'That introspective friend on a self-love journey. A lover ready for some fresh inspo. Your BFF who deserves the world and to feel really sexy . Your big sister, to thank her for years of answering your teenage questions about sex.'
  },
  {
    title: 'Has anyone ever given Dipsea to their mom?',
    answer: `If you’ve given Dipsea to your mom, we want to hear from you! No seriously, you’re incredible and clearly so is she: feedback@dipseastories.com.`
  },
  {
    title: 'Can I give Dipsea to myself?',
    answer: 'We highly recommend it. Just head to https://www.dipseastories.com/subscribe instead.'
  }
]

const CloseCircleSVG = (): React.Node => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38.6666 20C38.6666 30.3093 30.3092 38.6666 19.9999 38.6666C9.6906 38.6666 1.33325 30.3093 1.33325 20C1.33325 9.69066 9.6906 1.33331 19.9999 1.33331C30.3092 1.33331 38.6666 9.69066 38.6666 20Z"
      stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M27.6663 20L12.3329 20" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" />
  </svg>)

const OpenCircleSVG = (): React.Node => (
  <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38.6666 20.5C38.6666 30.8094 30.3092 39.1667 19.9999 39.1667C9.6906 39.1667 1.33325 30.8094 1.33325 20.5C1.33325 10.1907 9.6906 1.83337 19.9999 1.83337C30.3092 1.83337 38.6666 10.1907 38.6666 20.5Z"
      stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 28.1663L20 12.833" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M27.6663 20.5001L12.3329 20.5001" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" />
  </svg>)

export default function Faqs (): React.Node {
  return (
    <Section>
      <BackgroundGradient
        quadrant={2}
        overlayColor='magenta'
      />
      <FaqsContainer>
        <Container>
          <Row>
            <Col align="center" xl={8} lg={10} md={6} sm={8} xs={4} offset={{
              xl: 2,
              lg: 1,
              md: 1
            }}>
              <Subheader>Questions? We’ve got answers. </Subheader>
            </Col>
          </Row>
          <Row>
            <Col xl={8} lg={10} md={6} sm={8} xs={4} offset={{
              xl: 2,
              lg: 1,
              md: 1
            }}>
              <Hr size={1} />
            </Col>
          </Row>
          {questions.map((item: QuestionType, index: number): React.Node => {
            return (
              <Question answer={item.answer} title={item.title} key={`question-${index}`} />
            )
          })}
        </Container>
      </FaqsContainer>
    </Section>
  )
}

function Question ({ answer, title }: QuestionType): React.Node {
  const [isOpen, setIsOpen] = React.useState <boolean>(false)
  return (
    <Row>
      <Hidden xs={true} sm={true}>
        <Col
          xl={7} lg={9} md={5} xs={4}
          offset={{
            xl: 2,
            lg: 1,
            md: 1
          }}
          align="flex-start"
        >
          <H4 color={colors.white} onClick={(): void => setIsOpen(!isOpen)}
            style={{ cursor: 'pointer' }}>{title}</H4>
          {isOpen && <QuestionAnswer><LinkifyText>{answer}</LinkifyText></QuestionAnswer>}
        </Col>
        <Col xl={1} lg={1} md={1} align={'flex-end'} onClick={(): void => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
          {isOpen ? <CloseCircleSVG /> : <OpenCircleSVG />}
        </Col>
      </Hidden>
      <Hidden xl={true} lg={true} md={true} >
        <Col xs={3} align="flex-start" onClick={(): void => setIsOpen(!isOpen)}>
          <H4 color={colors.white} style={{ cursor: 'pointer' }}>{title}</H4>
        </Col>
        <Col xs={1} align="flex-end" onClick={(): void => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
          {isOpen ? <CloseCircleSVG /> : <OpenCircleSVG />}
        </Col>
        {isOpen && <Col xs={4}><QuestionAnswer><LinkifyText>{answer}</LinkifyText></QuestionAnswer></Col>}
      </Hidden>
      <Col xl={8} lg={10} md={6} sm={8} xs={4} offset={{
        xl: 2,
        lg: 1,
        md: 1
      }}>
        <Hr />
      </Col>
    </Row>
  )
}

const Section = styled.section`
  position: relative;
`
const Subheader = styled.div`
  text-align: center;
  width: 100%;
  font-size: 14px;
  line-height: 16px;
  text-transform: uppercase;
  color: ${colors.white};
  font-family: Good Sans Regular;
  margin-bottom: 40px;
  ${(props: any): string => config(props).media.sm`
    margin-bottom: 20px;
  `}
  ${(props: any): string => config(props).media.xl`
    margin-bottom: 25px;
  `}
`
const Hr = styled.hr`
  width: 100%;
  height: 1px;
  border: none;
  background-color: ${colors.white};
  margin-top: 20px;
  margin-bottom: 20px;
  ${(props: any): string => config(props).media.sm`
    margin-top: 25px;
    margin-bottom: 25px;
  `}
  ${(props: any): string => config(props).media.lg`
    margin-top: 35px;
    margin-bottom: 35px;
  `}
  ${(props: any): string => config(props).media.xl`
    margin-top: 37px;
    margin-bottom: 37px;
  `}
`
const FaqsContainer = styled.div`
  position: relative;
  overflow: hidden;
  padding-top: 70px;
  padding-bottom: 70px;
  z-index: 1;
  ${(props: any): string => config(props).media.sm`
    padding-top: 80px;
    padding-bottom: 80px;
  `}
  ${(props: any): string => config(props).media.md`
    padding-top: 80px;
    padding-bottom: 80px;
  `}
  ${(props: any): string => config(props).media.lg`
    padding-top: 100px;
    padding-bottom: 100px;
  `}
  ${(props: any): string => config(props).media.xl`
    padding-top: 120px;
    padding-bottom: 120px;
  `}
`
const QuestionAnswer = styled.div`
  font-family: Good Sans Light;
  font-size: 16px;
  line-height: 24px;
  margin-top: 7px;
  color: ${colors.white};
  ${(props: any): string => config(props).media.sm`
    margin-top: 10px;
    margin-bottom: 10px;
  `}
  a {
    color: ${colors.tintOrange};
    font-family: system-ui;
  }
`
